/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useId, type ReactNode, useCallback } from 'react';
import { css, cssMap, jsx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';

import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import type { FullPageEditorPresetAPI } from '@confluence/full-page-editor-store';

const svgStyles = css({
	overflow: 'hidden',
	pointerEvents: 'none',
	width: 16,
	height: 16,
	transform: 'rotate(90deg)',
});

/**
 * The generative sparkle glyph. `children` fill the star shape (via a clip path), which is how
 * the gradient is applied. `label` is for the rare case the icon carries meaning on its own —
 * leave it out and the icon is presentational.
 */
const SuggestionIcon = ({
	children,
	label,
}: {
	children?: ReactNode;
	label?: string;
}): JSX.Element => {
	const clipPathId = useId();
	return (
		<svg
			css={svgStyles}
			fill="none"
			viewBox="0 0 16 16"
			aria-label={label}
			role={label ? 'img' : 'presentation'}
		>
			<defs>
				<clipPath id={clipPathId}>
					<path d="M8.11719 1.00879C8.38352 1.05062 8.61063 1.23423 8.70508 1.49316L10.2549 5.74414L14.5068 7.29492C14.8027 7.40285 14.9998 7.6841 15 7.99902V8.00098C14.9998 8.3159 14.8027 8.59715 14.5068 8.70508L10.2549 10.2549L8.70508 14.5068C8.59715 14.8027 8.3159 14.9998 8.00098 15H7.99902C7.6841 14.9998 7.40285 14.8027 7.29492 14.5068L5.74414 10.2549L1.49316 8.70508C1.19728 8.59715 1.00018 8.3159 1 8.00098V7.99902C1.00018 7.6841 1.19728 7.40285 1.49316 7.29492L5.74414 5.74414L7.29492 1.49316L7.34277 1.38672C7.47321 1.15076 7.72343 1.00016 7.99902 1H8.00098L8.11719 1.00879Z" />
				</clipPath>
			</defs>
			<g clipPath={`url(#${clipPathId})`}>
				<foreignObject x="0" y="0" width="16" height="16" transform="rotate(-45 8 8)">
					{children}
				</foreignObject>
			</g>
		</svg>
	);
};

const messages = defineMessages({
	previewSuggestionButtonLabel: {
		id: 'inline-comments-common.preview-suggestion-button.label.non-final',
		defaultMessage: 'Preview a suggested edit',
		description:
			'Label for the button that opens a preview of an AI-suggested edit to the document.',
	},
});

// Distance the trailing arrow travels to the right on hover/pressed. Driven by a
// CSS variable on the button so the arrow can react to the button's state without
// selecting it as a descendant.
const arrowTranslateVar = '--preview-suggestion-arrow-translate';

const styles = cssMap({
	button: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: token('space.100'),
		width: '100%',
		paddingTop: token('space.150'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.200'),
		borderRadius: token('radius.large'),
		backgroundColor: token('color.background.neutral'),
		color: token('color.text'),
		transition: token('motion.button.hovered'),
		[arrowTranslateVar]: token('space.0'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.hovered'),
			[arrowTranslateVar]: token('space.050'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.pressed'),
			transition: token('motion.button.pressed'),
			[arrowTranslateVar]: token('space.050'),
		},
	},
	arrow: {
		display: 'flex',
		transform: `translateX(var(${arrowTranslateVar}))`,
		transition: `transform ${token('motion.duration.short')} ${token('motion.easing.out.practical')}`,
	},
	// Fills the SuggestionIcon's star glyph with the generative/suggestion gradient.
	iconGradient: {
		width: '100%',
		height: '100%',
		backgroundImage:
			// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
			'conic-gradient(from 90deg, rgba(252, 167, 0, 1) 0deg, rgba(252, 167, 0, 1) 90deg, rgba(106, 153, 34, 1) 90deg, rgba(106, 153, 34, 1) 180deg, rgba(24, 104, 219, 1) 180deg, rgba(24, 104, 219, 1) 270deg, rgba(175, 89, 224, 1) 270deg, rgba(175, 89, 224, 1) 360deg)',
	},
});

type PreviewSuggestionButtonProps = {
	/**
	 * The full-page editor preset API. `undefined` outside the full-page editor (e.g. renderer /
	 * view mode), where there is nothing to select, so the button does nothing.
	 */
	editorApi?: FullPageEditorPresetAPI;
	/** Called just before the suggestion is selected, e.g. to close the popup it was clicked in. */
	onPreview?: () => void;
	/**
	 * Id of the suggestion to open — the one generated from the comment thread rendering this
	 * button, as resolved by `usePreviewSuggestion`.
	 */
	suggestionId: string;
	testId?: string;
};

/**
 * A button that invites the user to preview an AI-suggested edit. Shows the generative
 * suggestion sparkle, a label, and a trailing arrow, with default/hover/pressed states.
 */
export function PreviewSuggestionButton({
	editorApi,
	testId,
	onPreview,
	suggestionId,
}: PreviewSuggestionButtonProps): JSX.Element {
	const { formatMessage } = useIntl();
	const label = formatMessage(messages.previewSuggestionButtonLabel);

	const handleClick = useCallback(() => {
		const selectSuggestion = editorApi?.aiSuggestions?.actions?.selectSuggestion;
		// Nothing to preview without the editor's suggestions API, so leave the surface as it is
		// rather than closing it for no reason.
		if (!selectSuggestion) {
			return;
		}
		onPreview?.();
		selectSuggestion(suggestionId);
	}, [editorApi, onPreview, suggestionId]);

	return (
		<Pressable xcss={styles.button} onClick={handleClick} testId={testId}>
			<Inline space="space.100" alignBlock="center">
				<SuggestionIcon>
					<Box xcss={styles.iconGradient} />
				</SuggestionIcon>
				<Text weight="medium">{label}</Text>
			</Inline>
			<Box xcss={styles.arrow}>
				<ArrowRightIcon label="" color={token('color.icon')} />
			</Box>
		</Pressable>
	);
}
